import { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Link,
} from "@mui/material";
import { Check, ChevronDown, ChevronUp, Circle } from "lucide-react";
import { FunctionCall, WebSource } from "components/hooks/useTaskAgentState";
import Duration from "components/common/Duration";
import useIsMobile from "components/hooks/useIsMobile";
import Spinner from "components/common/Spinner";
import LinkRouter from "components/navigation/LinkRouter";
import { ToolOutputsSection } from "../ToolOutputsSection";

const HIDE_CONTEXT_TOOL_NAMES = new Set([
  "create_list_document",
  "edit_list_document",
  "upsert_storage_document",
  "code_execution",
  "generate_document_image",
  "generate_image",
  "edit_image",
]);

const toPlainText = (value: unknown) => {
  const text = String(value ?? "");
  if (!/<\/?[a-z][\s\S]*>/i.test(text)) return text;

  const parser = new DOMParser();
  return parser.parseFromString(text, "text/html").body.textContent || "";
};

const summarizeArgument = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (["string", "number", "boolean"].includes(typeof value)) {
    return toPlainText(value);
  }
  if (Array.isArray(value)) {
    if (
      value.every((entry) =>
        ["string", "number", "boolean"].includes(typeof entry),
      )
    ) {
      return value.map(toPlainText).join(", ");
    }
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  return "structured details";
};

const DOCUMENT_PATH_PATTERN =
  /\/documents\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const LinkifiedText = ({ text }: { text: string }) => (
  <>
    {text
      .split(
        /((?:https?:\/\/[^\s<]+)?\/documents\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|https?:\/\/[^\s<]+)/gi,
      )
      .map((part, index) => {
        const documentPath = part.match(DOCUMENT_PATH_PATTERN)?.[0];
        const isExternalUrl = /^https?:\/\//i.test(part);
        if (!documentPath && !isExternalUrl) return part;

        if (documentPath) {
          return (
            <LinkRouter
              key={`${part}-${index}`}
              to={documentPath}
              inline
              useColorScheme
            >
              Link
            </LinkRouter>
          );
        }

        return (
          <Link
            key={`${part}-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </Link>
        );
      })}
  </>
);

const FunctionCallStatusIcon = ({
  state,
}: {
  state: "queued" | "running" | "completed";
}) => {
  const props = { size: 20 };
  switch (state) {
    case "completed":
      return <Check {...props} color="#8E9598" />;
    case "running":
      /* Spinner has a nested svg which causes extra spacing if using flex+gap
      Wrap it in a container so it's considered one element
      */
      return (
        <Box display="flex" alignItems="center">
          <Spinner {...props} />
        </Box>
      );
    default:
      return <Circle {...props} color="#9E9E9E" />;
  }
};

const Sources = ({ call }: { call: FunctionCall }) => {
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);

  if (!call.sources || call.sources.length === 0) return null;

  const maxVisible = isMobile ? 2 : 3;
  const hasMore = call.sources.length > maxVisible;
  const sources = (call.sources || []) as WebSource[];
  const visibleSources = showAll ? sources : sources.slice(0, maxVisible);
  const hiddenCount = sources.length - maxVisible;

  // Use google tool to get favicon URL.
  const getFavicon = (source: { uri: string; title: string }) => {
    const faviconUrl = `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(
      source.uri,
    )}&size=32`;
    return (
      <img
        src={faviconUrl}
        alt={source.title}
        style={{
          width: "19px",
          height: "19px",
          borderRadius: "50%",
        }}
      />
    );
  };
  return (
    <Box sx={{ marginTop: "4px", marginBottom: "8px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {visibleSources.map((source, index) => (
          <Chip
            key={`${source.uri}-${index}`}
            icon={getFavicon(source)}
            label={source.title}
            component="a"
            href={source.uri}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            clickable
            sx={{
              padding: "8px 4px",
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#e0e0e0",
              },
            }}
          />
        ))}
        {hasMore && !showAll && (
          <Chip
            label={`+${hiddenCount} more`}
            onClick={() => setShowAll(true)}
            clickable
            sx={{
              padding: "8px 4px",
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#e0e0e0",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export const DefaultActionContainer = ({
  call,
  displayName,
  actionSummary,
  children,
  defaultOpen,
  hideChevron = false,
}: {
  call: FunctionCall;
  displayName: string;
  actionSummary: string;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  hideChevron?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const isMobile = useIsMobile();
  const hasChildren = children !== undefined;
  const hasDetails =
    (hasChildren && Object.keys(call.args).length > 0) || call.result;
  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="4px"
        sx={{
          cursor: hasDetails ? "pointer" : "default",
        }}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <FunctionCallStatusIcon state={call.state} />
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap="8px"
          minWidth="0"
          flex="1"
        >
          <Typography
            variant="body"
            color={call.state === "running" ? "text.primary" : "text.secondary"}
            sx={{
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </Typography>
          {!isMobile && actionSummary && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0",
                minWidth: "0",
                flex: 1,
              }}
            >
              <Typography variant="bodySmall" color="text.secondary">
                {"("}
              </Typography>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight: "1px", // Prevent overlap with closing parenthesis
                }}
              >
                {actionSummary}
              </Typography>
              <Typography variant="bodySmall" color="text.secondary">
                {")"}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ minWidth: "fit-content" }}
        >
          {call.state === "running" && call.startTime && (
            <Duration startTime={new Date(call.startTime)} />
          )}
          {call.state === "completed" &&
            call.executionTimeSec &&
            call.executionTimeSec > 0.1 &&
            `${call.executionTimeSec.toFixed(1)}s`}
          {call.state === "queued" && `queued`}
        </Typography>
        {hasDetails && !hideChevron && (
          <IconButton
            size="small"
            sx={{ padding: "4px" }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronUp size={16} stroke="#8E9598" />
            ) : (
              <ChevronDown size={16} stroke="#8E9598" />
            )}
          </IconButton>
        )}
        {hideChevron && <Box sx={{ width: "24px", height: "20px" }} />}
      </Box>

      {hasDetails && (
        <Collapse in={isExpanded}>
          {children ||
            (call.result && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <LinkifiedText text={toPlainText(call.result)} />
              </Typography>
            ))}
        </Collapse>
      )}
    </Box>
  );
};

interface DefaultActionDisplayProps {
  call: FunctionCall;
  displayName: string;
  accountType?: "advisor" | "member";
}

export const DefaultActionDisplay = ({
  call,
  displayName,
  accountType = "advisor",
}: DefaultActionDisplayProps) => {
  const hidesContext = HIDE_CONTEXT_TOOL_NAMES.has(call.name);
  // Format arguments for inline display
  const formatArgs = (_args: Record<string, any>) => {
    // For now, lets default to no description if there's not a specific description that looks good.
    return "";
    // const entries = Object.entries(args);
    // if (entries.length === 0) return "";

    // const formatted = entries
    //   .map(([key, value]) => {
    //     const val = String(value);
    //     return val.length > 30
    //       ? `${key}: ${val.substring(0, 30)}...`
    //       : `${key}: ${val}`;
    //   })
    //   .join(", ");

    // return formatted.length > 80
    //   ? `${formatted.substring(0, 80)}...`
    //   : formatted;
  };

  const prettyArgs = () => {
    switch (call.name) {
      case "web_search":
      case "deep_research":
      case "perplexity_search":
      case "web_research":
        return call.args.query;
      case "read_qr_code":
        return "";
      case "fetch_forum_post":
        return "";
      case "url_thumbnail":
      case "url_thumbnails":
      case "web_fetch":
        return call.args.url;
      // case "read_file":
      //   return call.args.file_id;
      default:
        return formatArgs(call.args);
    }
  };
  const argsStr = prettyArgs();
  const showDetails = accountType === "advisor";
  const resultText = toPlainText(call.result);
  const hasAttachments = Boolean(call.attachments?.length);
  const children =
    showDetails ||
    hidesContext ||
    hasAttachments ||
    (call.sources && call.sources.length > 0) ? (
      <Box sx={{ marginTop: "8px", paddingLeft: "28px" }}>
        {showDetails && !hidesContext && (
          <>
            {Object.keys(call.args).length > 0 && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                {Object.entries(call.args)
                  .map(([key, value]) => `${key}: ${summarizeArgument(value)}`)
                  .join(", ")}
              </Typography>
            )}
          </>
        )}
        {(showDetails || hidesContext) && resultText && (
          <Typography
            variant="bodySmall"
            color="text.primary"
            sx={{
              backgroundColor: "#f5f5f5",
              padding: "8px",
              borderRadius: "4px",
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
            }}
          >
            <strong>Result:</strong> <LinkifiedText text={resultText} />
          </Typography>
        )}
        {hasAttachments && (
          <ToolOutputsSection
            functionCalls={[call]}
            includeDocuments={false}
            includeAllAttachments
            compact
          />
        )}
        <Sources call={call} />
      </Box>
    ) : undefined;

  return (
    <Box>
      <DefaultActionContainer
        call={call}
        displayName={displayName}
        actionSummary={argsStr}
      >
        {children}
      </DefaultActionContainer>
    </Box>
  );
};
